import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import GenericInvoicedToggle from "@/components/GenericInvoicedToggle";
import { emitServicesChanged } from "@/lib/servicesSync";
import { toast } from "sonner";
import { ClockCounterClockwise, House, Headset, ShieldCheck, CurrencyEur, Briefcase, Trash } from "@phosphor-icons/react";

const eur = (n) => (n ?? 0).toLocaleString("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 2 });

const KIND_META = {
  workshop: { label: "Officina", icon: ClockCounterClockwise, cls: "bg-primary/15 text-primary border-primary/30" },
  home: { label: "Domicilio", icon: House, cls: "bg-blue-500/15 text-blue-700 dark:text-blue-400 border-blue-500/30" },
  assistance: { label: "Assistenza", icon: Headset, cls: "bg-purple-500/15 text-purple-700 dark:text-purple-400 border-purple-500/30" },
  service: { label: "Servizio", icon: ShieldCheck, cls: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/30" },
  work: { label: "Lavori", icon: Briefcase, cls: "bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/30" },
};

function KindBadge({ kind }) {
  const meta = KIND_META[kind] || KIND_META.workshop;
  const Icon = meta.icon;
  return (
    <Badge className={`${meta.cls} hover:${meta.cls} border font-mono text-[10px] uppercase gap-1`}>
      <Icon size={11} weight="fill" /> {meta.label}
    </Badge>
  );
}

const FILTERS = [
  { k: "all", l: "Tutti" },
  { k: "workshop", l: "Officina" },
  { k: "home", l: "Domicilio" },
  { k: "assistance", l: "Assistenze" },
  { k: "service", l: "Servizi" },
  { k: "work", l: "Lavori" },
];

function formatDate(dateStr) {
  if (!dateStr) return "—";
  const s = dateStr.length >= 10 ? dateStr.slice(0, 10) : dateStr;
  const [y, m, d] = s.split("-");
  return `${d}/${m}/${y}`;
}

function patchUrlFor(item) {
  if (item.kind === "workshop" || item.kind === "home") return `/interventions/${item.id}`;
  if (item.kind === "assistance") return `/assistance-logs/${item.id}`;
  if (item.kind === "work") return `/works/${item.id}`;
  if (item.kind === "service") {
    if (item.history_index !== null && item.history_index !== undefined) {
      return `/services/${item.service_id}/history/${item.history_index}`;
    }
    return `/services/${item.service_id}`;
  }
  return null;
}

function deletePlanFor(item) {
  // Returns { url, hard, warning } describing how to delete this ops-feed row.
  if (item.kind === "assistance") {
    return { url: `/assistance-logs/${item.id}`, warning: `Rimuovere questa assistenza da ${item.customer_surname || ""} ${item.customer_name || ""} per ${item.price ? item.price.toFixed(2) + " €" : "—"}?\n\nIl movimento sparirà da dashboard e dallo storico del cliente.` };
  }
  if (item.kind === "work") {
    return { url: `/works/${item.id}`, warning: `Eliminare definitivamente questo lavoro (${item.description || ""}) per ${item.price ? item.price.toFixed(2) + " €" : "—"}?\n\nIl fatturato lavori verrà aggiornato.` };
  }
  if (item.kind === "workshop" || item.kind === "home") {
    return { url: `/interventions/${item.id}`, warning: `Eliminare l'intervento ${item.code || ""}?\n\nSarà rimosso da dashboard e dalla scheda cliente. Operazione irreversibile.` };
  }
  if (item.kind === "service") {
    if (item.history_index !== null && item.history_index !== undefined) {
      return { url: `/services/${item.service_id}/history/${item.history_index}`, warning: `Annullare questo rinnovo di ${item.price ? item.price.toFixed(2) + " €" : "—"}?\n\nSarà rimosso dallo storico del cliente e dalla dashboard.` };
    }
    // creazione: hard delete
    return {
      url: `/services/${item.service_id}?hard=true`,
      warning: `Eliminare completamente il servizio "${item.description || ""}"?\n\nRimuove il servizio + tutto lo storico dei rinnovi. Operazione irreversibile.`,
    };
  }
  return null;
}

const PAGE_SIZE = 10;

export default function OperationsFeed({ year }) {
  const [data, setData] = useState({ items: [], total: 0, count: 0 });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [page, setPage] = useState(1);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { year };
      if (filter !== "all") params.op_type = filter;
      const { data } = await api.get("/stats/operations", { params });
      setData(data);
    } finally {
      setLoading(false);
    }
  }, [year, filter]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [filter, year]);

  const handleInvoicedChange = (item, updated) => {
    // For services, updated is the full service; for others it's the item itself.
    // We just re-toggle the current row's invoiced flag optimistically already, so just reflect it here.
    setData((prev) => ({
      ...prev,
      items: prev.items.map((x) => x.id === item.id ? { ...x, invoiced: !x.invoiced } : x),
    }));
  };

  const handleDelete = async (item) => {
    const plan = deletePlanFor(item);
    if (!plan) return;
    if (!window.confirm(plan.warning)) return;
    try {
      await api.delete(plan.url);
      toast.success("Movimento eliminato — dashboard aggiornata");
      // If it's a service, notify customer pages listening for services changes
      if (item.kind === "service" && item.customer_id) {
        emitServicesChanged(item.customer_id);
      }
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const totalPages = Math.max(1, Math.ceil(data.items.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageStart = (currentPage - 1) * PAGE_SIZE;
  const pageItems = data.items.slice(pageStart, pageStart + PAGE_SIZE);

  return (
    <Card className="border-border/70 rounded-lg overflow-hidden" data-testid="operations-feed">
      <div className="px-4 py-3 border-b border-border/60 bg-secondary/40 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <CurrencyEur size={20} weight="duotone" className="text-primary" />
          <div>
            <h2 className="font-display font-bold tracking-tight text-lg">Movimenti {year}</h2>
            <div className="text-xs text-muted-foreground font-mono">
              Tutte le entrate registrate — indipendentemente dallo stato "Fatturato"
            </div>
          </div>
        </div>
        <div className="flex gap-1 bg-secondary rounded-md p-1 flex-wrap">
          {FILTERS.map((f) => (
            <button
              key={f.k}
              onClick={() => setFilter(f.k)}
              data-testid={`ops-filter-${f.k}`}
              className={`px-3 py-1.5 rounded text-xs font-medium ${
                filter === f.k ? "bg-background shadow-sm" : "text-muted-foreground hover:text-foreground"
              }`}
              style={{ transition: "background-color 0.15s ease, color 0.15s ease" }}
            >
              {f.l}
            </button>
          ))}
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-secondary/30">
            <tr className="text-left">
              <th className="px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Data</th>
              <th className="px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Tipo</th>
              <th className="px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Cliente</th>
              <th className="px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Descrizione</th>
              <th className="px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-muted-foreground text-right">Prezzo</th>
              <th className="px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Fattura</th>
              <th className="px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-muted-foreground text-right w-10"></th>
            </tr>
          </thead>
          <tbody data-testid="operations-feed-body">
            {loading && (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">Caricamento…</td></tr>
            )}
            {!loading && data.items.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-10 text-center text-muted-foreground font-mono text-sm">
                — Nessun movimento registrato per l'anno {year} —
              </td></tr>
            )}
            {pageItems.map((it) => {
              const url = patchUrlFor(it);
              return (
                <tr key={`${it.kind}-${it.id}`} className="border-t border-border/50 hover:bg-secondary/40"
                    style={{ transition: "background-color 0.15s ease" }}
                    data-testid={`ops-row-${it.kind}-${it.id}`}>
                  <td className="px-4 py-3 font-mono text-xs text-muted-foreground whitespace-nowrap">{formatDate(it.date)}</td>
                  <td className="px-4 py-3"><KindBadge kind={it.kind} /></td>
                  <td className="px-4 py-3">
                    {it.customer_id ? (
                      <Link to={`/clienti/${it.customer_id}`} className="font-medium hover:text-primary">
                        {it.customer_surname} {it.customer_name}
                      </Link>
                    ) : (
                      <span className="font-medium text-muted-foreground">—</span>
                    )}
                    {it.code && <div className="font-mono text-[10px] text-muted-foreground">{it.code}</div>}
                  </td>
                  <td className="px-4 py-3 max-w-[380px]">
                    {it.ref_url ? (
                      <Link to={it.ref_url} className="hover:text-primary">
                        <div className="truncate">{it.description || "—"}</div>
                      </Link>
                    ) : (
                      <div className="truncate">{it.description || "—"}</div>
                    )}
                  </td>
                  <td className="px-4 py-3 font-mono text-right whitespace-nowrap font-semibold">{eur(it.price)}</td>
                  <td className="px-4 py-3">
                    {url ? (
                      <GenericInvoicedToggle
                        key={`toggle-${it.kind}-${it.id}-${it.invoiced}`}
                        initialInvoiced={it.invoiced}
                        patchUrl={url}
                        testId={`ops-invoiced-${it.kind}-${it.id}`}
                        onChange={() => handleInvoicedChange(it)}
                      />
                    ) : null}
                  </td>
                  <td className="px-2 py-3 text-right">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleDelete(it)}
                      data-testid={`ops-delete-${it.kind}-${it.id}`}
                      className="h-8 w-8 p-0 text-destructive hover:text-destructive hover:bg-destructive/10"
                      title="Elimina questo movimento"
                    >
                      <Trash size={14} />
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
          {!loading && data.items.length > 0 && (
            <tfoot className="bg-secondary/60 border-t-2 border-border">
              <tr>
                <td colSpan={4} className="px-4 py-3 font-mono text-xs uppercase tracking-widest text-muted-foreground">
                  {data.count} movimenti totali
                </td>
                <td className="px-4 py-3 font-mono text-right font-bold text-base" data-testid="ops-total">
                  {eur(data.total)}
                </td>
                <td colSpan={2}></td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {!loading && totalPages > 1 && (
        <div className="border-t border-border/60 bg-secondary/30 px-4 py-3 flex items-center justify-between" data-testid="ops-pagination">
          <div className="font-mono text-xs text-muted-foreground">
            Pagina {currentPage} di {totalPages} — {pageStart + 1}-{Math.min(pageStart + PAGE_SIZE, data.items.length)} di {data.items.length}
          </div>
          <div className="flex gap-1">
            <button
              onClick={() => setPage(1)}
              disabled={currentPage === 1}
              data-testid="ops-page-first"
              className="px-3 py-1 rounded text-xs font-mono border border-border bg-background hover:bg-secondary disabled:opacity-40 disabled:cursor-not-allowed"
            >« Prima</button>
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              data-testid="ops-page-prev"
              className="px-3 py-1 rounded text-xs font-mono border border-border bg-background hover:bg-secondary disabled:opacity-40 disabled:cursor-not-allowed"
            >‹ Prec.</button>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              data-testid="ops-page-next"
              className="px-3 py-1 rounded text-xs font-mono border border-border bg-background hover:bg-secondary disabled:opacity-40 disabled:cursor-not-allowed"
            >Succ. ›</button>
            <button
              onClick={() => setPage(totalPages)}
              disabled={currentPage === totalPages}
              data-testid="ops-page-last"
              className="px-3 py-1 rounded text-xs font-mono border border-border bg-background hover:bg-secondary disabled:opacity-40 disabled:cursor-not-allowed"
            >Ultima »</button>
          </div>
        </div>
      )}
    </Card>
  );
}
